import { useEffect, useRef, useState } from 'react';
import { api } from 'src/services/api';
import { showToastError } from 'src/utilities/errortoast';

const MAX_KEYWORD_LENGTH = 200;
const MAX_KEYWORDS = 20;

type NormalizedPreferences = {
  instructions: string;
  keywords: string[];
};

type RawPreferences = {
  instructions?: string;
  custom_knowledge?: string;
  keywords?: unknown;
  custom_keywords?: unknown;
};

const normalizePreferences = (
  preferences: RawPreferences = {}
): NormalizedPreferences => ({
  instructions: preferences.instructions || preferences.custom_knowledge || '',
  keywords: Array.isArray(preferences.keywords)
    ? (preferences.keywords as string[])
    : Array.isArray(preferences.custom_keywords)
      ? (preferences.custom_keywords as string[])
      : [],
});

export const getTotalKeywordLength = (keywordList: string[]): number =>
  keywordList.reduce((total, keyword) => total + keyword.length, 0);

type OnSaveSuccessParams = {
  hasInstructionsChanged: boolean;
  hasKeywordsChanged: boolean;
  instructions: string;
  keywords: string[];
};

type UseMemoryPreferencesParams = {
  endpoint: string;
  canLoad?: boolean;
  canSave?: boolean;
  fallbackPreferences?: RawPreferences;
  fetchErrorLogMessage?: string;
  saveErrorLogMessage?: string;
  saveErrorToastMessage?: string;
  onSaveSuccess?: (params: OnSaveSuccessParams) => void;
  onAddKeyword?: (keyword: string, totalKeywords: number) => void;
};

type UseMemoryPreferencesReturn = {
  currentKeywordInput: string;
  handleAddKeyword: () => void;
  handleRemoveKeyword: (keyword: string) => void;
  instructions: string;
  keywordError: string;
  keywords: string[];
  setCurrentKeywordInput: (value: string) => void;
  setInstructions: (value: string) => void;
};

export const useMemoryPreferences = ({
  endpoint,
  canLoad = true,
  canSave = true,
  fallbackPreferences,
  fetchErrorLogMessage,
  saveErrorLogMessage,
  saveErrorToastMessage,
  onSaveSuccess,
  onAddKeyword,
}: UseMemoryPreferencesParams): UseMemoryPreferencesReturn => {
  const [instructions, setInstructions] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [currentKeywordInput, setCurrentKeywordInput] = useState('');
  const [keywordError, setKeywordError] = useState('');
  const [savedPreferences, setSavedPreferences] =
    useState<NormalizedPreferences>({
      instructions: '',
      keywords: [],
    });
  const [isLoaded, setIsLoaded] = useState(false);
  const [didRemoteFetchFail, setDidRemoteFetchFail] = useState(false);

  const onSaveSuccessRef = useRef(onSaveSuccess);
  onSaveSuccessRef.current = onSaveSuccess;
  const onAddKeywordRef = useRef(onAddKeyword);
  onAddKeywordRef.current = onAddKeyword;

  const fallback = normalizePreferences(fallbackPreferences);

  const applyPreferences = (nextPreferences: NormalizedPreferences) => {
    setInstructions(nextPreferences.instructions);
    setKeywords(nextPreferences.keywords);
    setSavedPreferences(nextPreferences);
  };

  useEffect(() => {
    if (!canLoad) {
      return;
    }

    let isMounted = true;

    const fetchPreferences = async () => {
      try {
        const result = await api.get<RawPreferences>(endpoint);
        if (!isMounted) {
          return;
        }

        applyPreferences(normalizePreferences(result?.data));
        setDidRemoteFetchFail(false);
      } catch (e) {
        console.error(fetchErrorLogMessage, e);
        if (!isMounted) {
          return;
        }

        applyPreferences(fallback);
        setDidRemoteFetchFail(true);
      } finally {
        if (isMounted) {
          setIsLoaded(true);
        }
      }
    };

    fetchPreferences();

    return () => {
      isMounted = false;
    };
  }, [canLoad, endpoint]);

  useEffect(() => {
    if (!didRemoteFetchFail || !isLoaded) {
      return;
    }

    const hasLocalChanges =
      instructions !== savedPreferences.instructions ||
      JSON.stringify(keywords) !== JSON.stringify(savedPreferences.keywords);

    if (hasLocalChanges) {
      return;
    }

    const fallbackChanged =
      fallback.instructions !== savedPreferences.instructions ||
      JSON.stringify(fallback.keywords) !==
        JSON.stringify(savedPreferences.keywords);

    if (fallbackChanged) {
      applyPreferences(fallback);
    }
  }, [
    didRemoteFetchFail,
    fallback.instructions,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(fallback.keywords),
    instructions,
    isLoaded,
    keywords,
    savedPreferences,
  ]);

  useEffect(() => {
    const hasInstructionsChanged =
      instructions !== savedPreferences.instructions;
    const hasKeywordsChanged =
      JSON.stringify(keywords) !== JSON.stringify(savedPreferences.keywords);

    if (
      !canSave ||
      !isLoaded ||
      (!hasInstructionsChanged && !hasKeywordsChanged)
    ) {
      return;
    }

    const handler = setTimeout(async () => {
      try {
        const result = await api.put<RawPreferences>(endpoint, {
          custom_knowledge: instructions,
          custom_keywords: keywords,
        });

        const nextPreferences = normalizePreferences(result?.data);
        setSavedPreferences(nextPreferences);
        onSaveSuccessRef.current?.({
          hasInstructionsChanged,
          hasKeywordsChanged,
          instructions,
          keywords,
        });
      } catch (e) {
        console.error(saveErrorLogMessage, e);
        showToastError(saveErrorToastMessage);
      }
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [
    canSave,
    endpoint,
    instructions,
    isLoaded,
    keywords,
    saveErrorLogMessage,
    saveErrorToastMessage,
    savedPreferences,
  ]);

  const handleAddKeyword = () => {
    const newKeyword = currentKeywordInput.trim();

    if (keywords.length >= MAX_KEYWORDS) {
      const errorMessage = `Maximum of ${MAX_KEYWORDS} keywords allowed`;
      setKeywordError(errorMessage);
      showToastError(errorMessage);
      return;
    }

    const currentTotalLength = getTotalKeywordLength(keywords);
    if (currentTotalLength + newKeyword.length > MAX_KEYWORD_LENGTH) {
      const errorMessage = `Total character limit of ${MAX_KEYWORD_LENGTH} exceeded`;
      setKeywordError(errorMessage);
      showToastError(errorMessage);
      return;
    }

    if (newKeyword && !keywords.includes(newKeyword)) {
      setKeywords([...keywords, newKeyword]);
      onAddKeywordRef.current?.(newKeyword, keywords.length + 1);
      setKeywordError('');
    }

    setCurrentKeywordInput('');
  };

  const handleRemoveKeyword = (removedKeyword: string) => {
    setKeywords(keywords.filter((keyword) => keyword !== removedKeyword));
    setKeywordError('');
  };

  return {
    currentKeywordInput,
    handleAddKeyword,
    handleRemoveKeyword,
    instructions,
    keywordError,
    keywords,
    setCurrentKeywordInput,
    setInstructions,
  };
};
